import React from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AssetsAdvancedFilters } from "@/components/assets/AssetsAdvancedFilters";

interface AssetsToolbarProps {
  filters: any;
  updateFilters: (patch: any) => void;
  resetFilters: () => void;
  filterOptions: { currencies: string[] };
  totalFilteredCount: number;
  totalCount: number;
}

export function AssetsToolbar({
  filters,
  updateFilters,
  resetFilters,
  filterOptions,
  totalFilteredCount,
  totalCount,
}: AssetsToolbarProps) {
  const hasActiveFilters =
    totalFilteredCount !== totalCount ||
    !!filters.search ||
    (filters.assetTypes?.length || 0) > 0 ||
    (filters.currencies?.length || 0) > 0 ||
    filters.valueRange.min !== null ||
    filters.valueRange.max !== null ||
    filters.dateRange.start !== null ||
    filters.dateRange.end !== null;

  return (
    <div className="w-full">
      {/* Search and filter controls */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-3">
        {/* Search Input */}
        <div className="relative w-full min-w-0">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search assets by name, symbol, or type..."
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
            className="pl-10 w-full min-h-[44px]"
          />
        </div>

        {/* Filters Button */}
        <div className="w-full sm:w-auto">
          <AssetsAdvancedFilters
            filters={filters}
            onFiltersChange={updateFilters}
            onReset={resetFilters}
            availableCurrencies={filterOptions.currencies}
          />
        </div>
      </div>

      {/* Filter status badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center justify-between sm:justify-end gap-2 mt-2">
          {totalFilteredCount !== totalCount && (
            <Badge variant="secondary">
              {totalFilteredCount} of {totalCount}
            </Badge>
          )}
          {(filters.search ||
            (filters.assetTypes?.length || 0) > 0 ||
            (filters.currencies?.length || 0) > 0 ||
            filters.valueRange.min !== null ||
            filters.valueRange.max !== null ||
            filters.dateRange.start !== null ||
            filters.dateRange.end !== null) && (
            <Button variant="outline" size="sm" onClick={resetFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
